import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../../lib/api';
import { toast } from 'sonner';
import { formatDateTime } from '../../../lib/format';
import { Settings, Save, AlertTriangle, ShieldAlert, KeyRound, Power, UserPlus, Clock } from 'lucide-react';

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <div className="text-sm font-medium text-slate-800">{label}</div>
    {hint && <div className="text-xs text-slate-500">{hint}</div>}
    {children}
  </div>
);

const Toggle = ({ checked, onChange, testId }) => (
  <button data-testid={testId} type="button" onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#3b4872]' : 'bg-slate-300'}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

export default function VendorPortalSettingsPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/vendor-portal-admin/settings').then(d => { setS(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const payload = {
        registration_open: !!s.registration_open,
        require_admin_approval_for_signup: !!s.require_admin_approval_for_signup,
        max_login_attempts_before_lockout: Number(s.max_login_attempts_before_lockout) || 5,
        lockout_duration_minutes: Number(s.lockout_duration_minutes) || 15,
        session_timeout_minutes: Number(s.session_timeout_minutes) || 60,
        maintenance_mode: !!s.maintenance_mode,
        maintenance_message: s.maintenance_message || '',
      };
      const updated = await apiPut('/vendor-portal-admin/settings', payload);
      setS(updated);
      toast.success('Portal settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !s) {
    return <div data-testid="settings-loading" className="p-6 text-slate-500">Loading settings…</div>;
  }

  return (
    <div data-testid="vendor-portal-settings" className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Vendor Portal Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Settings size={26} /> Portal Settings
        </h1>
        <p className="text-sm text-slate-600 mt-1">Global toggles applied to every vendor portal user.</p>
      </div>

      {/* Maintenance section */}
      <div className="rounded-sm border border-slate-200 bg-white p-5 space-y-4">
        <div className="font-display font-bold text-[#3b4872] flex items-center gap-2"><ShieldAlert size={18} /> Maintenance Mode</div>
        <Field label="Block all vendor logins and registrations"
          hint="Useful during deployments or data migrations. Returns 503 with the message below.">
          <div className="flex items-center gap-3">
            <Toggle checked={!!s.maintenance_mode} onChange={v => set('maintenance_mode', v)} testId="toggle-maintenance" />
            <span className={`text-sm font-bold ${s.maintenance_mode ? 'text-red-600' : 'text-slate-400'}`}>
              {s.maintenance_mode ? 'ON — vendors are blocked' : 'OFF — vendors can log in'}
            </span>
          </div>
        </Field>
        <Field label="Maintenance message" hint="Shown to vendors who try to log in. Keep it brief and informative.">
          <textarea data-testid="maintenance-message" value={s.maintenance_message || ''}
            onChange={e => set('maintenance_message', e.target.value)}
            placeholder="The vendor portal is currently undergoing scheduled maintenance and will be back online by 14:00 IST."
            className="w-full border border-slate-300 rounded-sm p-2 text-sm" rows={3} />
        </Field>
      </div>

      {/* Registration */}
      <div className="rounded-sm border border-slate-200 bg-white p-5 space-y-4">
        <div className="font-display font-bold text-[#3b4872] flex items-center gap-2"><UserPlus size={18} /> Registration</div>
        <Field label="Allow new vendor self-registration"
          hint="When OFF, the /partner/register endpoint returns 403 — new vendors must be invited by procurement manually.">
          <div className="flex items-center gap-3">
            <Toggle checked={!!s.registration_open} onChange={v => set('registration_open', v)} testId="toggle-registration" />
            <span className="text-sm">{s.registration_open ? 'Open' : 'Closed'}</span>
          </div>
        </Field>
        <Field label="Require admin approval after PQ submission"
          hint="When ON (recommended), submitted PQ applications wait for procurement review before the vendor can transact. When OFF, applications are auto-approved (still requires email verification first).">
          <div className="flex items-center gap-3">
            <Toggle checked={!!s.require_admin_approval_for_signup} onChange={v => set('require_admin_approval_for_signup', v)} testId="toggle-approval" />
            <span className="text-sm">{s.require_admin_approval_for_signup ? 'Required' : 'Auto-approve'}</span>
          </div>
        </Field>
      </div>

      {/* Security */}
      <div className="rounded-sm border border-slate-200 bg-white p-5 space-y-4">
        <div className="font-display font-bold text-[#3b4872] flex items-center gap-2"><KeyRound size={18} /> Login Security</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Max failed login attempts before lockout"
            hint="3–10 is typical. Below 3 will lock out users on a typo; above 10 weakens brute-force protection.">
            <input data-testid="max-attempts" type="number" min={1} max={50}
              value={s.max_login_attempts_before_lockout || 5}
              onChange={e => set('max_login_attempts_before_lockout', Number(e.target.value))}
              className="w-full border border-slate-300 rounded-sm p-2 text-sm" />
          </Field>
          <Field label="Lockout duration (minutes)" hint="How long the account is blocked after the limit is hit.">
            <input data-testid="lockout-duration" type="number" min={1} max={1440}
              value={s.lockout_duration_minutes || 15}
              onChange={e => set('lockout_duration_minutes', Number(e.target.value))}
              className="w-full border border-slate-300 rounded-sm p-2 text-sm" />
          </Field>
          <Field label={<span className="inline-flex items-center gap-1"><Clock size={13} /> Session timeout (minutes)</span>}
            hint="Vendor JWT expiry. 1440 = 24 hours. Lower for stricter security, higher for convenience.">
            <input data-testid="session-timeout" type="number" min={15} max={43200}
              value={s.session_timeout_minutes || 60}
              onChange={e => set('session_timeout_minutes', Number(e.target.value))}
              className="w-full border border-slate-300 rounded-sm p-2 text-sm" />
          </Field>
        </div>
      </div>

      {s.maintenance_mode && (
        <div className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <b>Maintenance mode is ENABLED.</b> Save to apply — all new vendor login/registration requests will return 503 immediately.
          </div>
        </div>
      )}

      <div className="flex justify-end items-center gap-3">
        {s.updated_at && (
          <div className="text-xs text-slate-500">
            Last updated {formatDateTime(s.updated_at)} by {s.updated_by || 'system'}
          </div>
        )}
        <button data-testid="save-settings" onClick={save} disabled={saving}
          className="px-4 py-2 bg-[#3b4872] text-white rounded-sm font-bold text-sm hover:bg-[#2d3858] disabled:opacity-60 flex items-center gap-2">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
